import i18n from "@plugins/i18n";
import studentTooltipUtils from "@plugins/student-tooltip/student.tooltip.utils";
import { createApp } from "vue";
import StudentTooltipModal from "@plugins/student-tooltip/components/StudentTooltipModal.vue";
import DOMUtils from "@utils/dom.utils";
import StudentTooltip from "@plugins/student-tooltip/components/StudentTooltip.vue";
import richTextUtils from "@plugins/richtext/utils/richtext.utils";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import studentToolTipEventBus from "@plugins/richtext/plugins/student-tooltip/service/event-bus";

export default function (editor) {
  const TOOLTIP_COMPONENT_WRAPPER_SELECTOR =
    studentTooltipUtils.SELECTOR.STUDENT_TOOLTIP_COMPONENT_WRAPPER;
  const TOOLTIP_COMPONENT_SELECTOR = studentTooltipUtils.SELECTOR.STUDENT_TOOLTIP_COMPONENT;
  const TOOLTIP_SELECTOR = studentTooltipUtils.SELECTOR.STUDENT_TOOLTIP;
  const TOOLTIP_TAG_SELECTOR = studentTooltipUtils.SELECTOR.STUDENT_TOOLTIP_TAG;
  const richTextEditor = editor.options.get("richTextEditor");

  let modalId = null;

  (function init() {
    // register the rich text common functions for StudentTooltip.
    richTextEditor.plugins["student_tooltip"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
    };
  })();

  /**
   * Function to convert existing StudentTooltip tag to StudentTooltip component.
   */
  function renderInEditor() {
    DOMUtils.elOf(editor.getBody(), TOOLTIP_TAG_SELECTOR).forEach((element) => {
      // Retrieve data to create the Student tooltip component
      const tooltipData = studentTooltipUtils.retrieveDataFromStudentTooltip(element);
      const StudentTooltipApp = createApp(StudentTooltip, {
        ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
        tooltipData: tooltipData,
        showTooltip: false,
      });
      AppInstanceUtils.installCommonPlugin(StudentTooltipApp);

      // Wrap a non-editable layer
      let wrapper = DOMUtils.createElement("span", {
        class: "sls-student-tooltip-wrapper",
        contenteditable: false,
      });
      element.parentNode.insertBefore(wrapper, element);
      wrapper.appendChild(element);

      // Replace created student tooltip component with the element
      const richTextStudentToolTip = StudentTooltipApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(richTextStudentToolTip);
      // Handle case when plugin component is last item,
      // add a paragraph to allow user to continue adding content.
      const tooltipWrapper = richTextStudentToolTip.$el.closest(TOOLTIP_COMPONENT_WRAPPER_SELECTOR);
      richTextUtils.addNewLineIfIsLastElement(tooltipWrapper);
    });
  }

  /**
   * Function to clean up the rich text content.
   * Convert Student Tooltip component to student-tooltip tag for saving.
   *
   * @param content
   * @returns {*}
   */
  function cleanUp(content) {
    let container = document.createElement("div");
    container.innerHTML = content;
    let clone = container.cloneNode(true);

    // Find StudentTooltip component, extract data to construct tag, replace tag with wrapper
    DOMUtils.elOf(clone, TOOLTIP_COMPONENT_SELECTOR).forEach((element) => {
      let data = studentTooltipUtils.retrieveDataFromStudentTooltip(element);
      let studentTooltipTag = studentTooltipUtils.createStudentTooltipTag(data);
      let studentTooltipWrapper = element.closest(TOOLTIP_COMPONENT_WRAPPER_SELECTOR);
      if (studentTooltipWrapper) {
        studentTooltipWrapper.replaceWith(studentTooltipTag);
      }
    });

    return clone.innerHTML;
  }

  /**
   * Function to determine if the rich text editor contains student tooltip element(s).
   * @param container
   * @returns {boolean}
   */
  function containPluginElements(container) {
    return container.querySelectorAll(TOOLTIP_SELECTOR).length > 0;
  }

  /**
   * Register the StudentTooltip plugin button to the rich text editor.
   */
  editor.ui.registry.addToggleButton("student_tooltip", {
    tooltip: i18n.t("tinymce_plugin_student_tooltip_icon_button_tooltip"),
    icon: "student-tooltip",
    onAction: handleClickAction,
    onSetup: handleStudentTooltipElementSelected,
  });

  /**
   * Function to handle StudentTooltip button clicked.
   */
  function handleClickAction() {
    // get selected student tooltip (if exist)
    const targetNode = studentTooltipUtils.retrieveFirstSelectedStudentTooltipFromEditor(editor);

    if (notNull(targetNode)) {
      // selected student tooltip existing, edit
      const tooltipData = studentTooltipUtils.retrieveDataFromStudentTooltip(targetNode);
      openModal(tooltipData);
    } else {
      // no selected student tooltip, create

      // For iOS and ipadOS, using the on-screen word suggestion will result in
      // the caret position to not be updated.
      editor.save();

      openModal();
    }
  }

  /**
   * Function to make the StudentTooltip toolbar tool-icon active when an StudentTooltip element has been
   * selected in the editor's content.
   * Reference: https://www.tiny.cloud/docs/ui-components/typesoftoolbarbuttons/#togglebutton
   *
   * @param buttonApi
   * @returns {function(): *}
   */
  function handleStudentTooltipElementSelected(buttonApi) {
    const nodeChangeHandler = function (eventApi) {
      buttonApi.setActive(
        // to determine if the selection has STP element.
        eventApi.parents.some((parent) => {
          return editor.dom.hasClass(parent, "sls-student-tooltip-wrapper");
        }),
      );
    };

    // onSetup should always return the unbind handlers.
    editor.on("NodeChange", nodeChangeHandler);
    return function () {
      return editor.off("NodeChange", nodeChangeHandler);
    };
  }

  /**
   * Function to open the Student Tooltip settings modal to create or update StudentTooltip tooltip.
   */
  function openModal(selectedTooltip) {
    // event bus handler between editor and modal
    let eventBusWatchInstances = [];
    eventBusWatchInstances.push(
      studentToolTipEventBus.$on("addStudentTooltip", handleAddStudentTooltip),
    );

    // show the modal
    modalId = richTextEditor.$modal.show(
      StudentTooltipModal,
      {
        eventBusWatchInstances: eventBusWatchInstances,
        selectedTooltip: selectedTooltip,
      },
      {},
      true,
    );
  }

  /**
   * Function to handle the insert/update of Student Tooltip into the editor.
   *
   * @param tooltipData
   */
  function handleAddStudentTooltip(tooltipData) {
    // close the modal
    richTextEditor.$modal.remove(modalId);

    // create a placeholder to be mounted
    let studentTooltipTag = studentTooltipUtils.createStudentTooltipTag(tooltipData);
    editor.undoManager.ignore(() => {
      editor.insertContent(studentTooltipTag.outerHTML);
    });

    // render Student Tooltip into the editor
    renderInEditor();
    setTimeout(() => {
      editor.save();
    }, 100);
  }
}
